/**
 * @fileoverview Connect administrator deep links to independently owned workspaces. People owns staff lifecycle; Phone and its dashboard own routing and call observations; AiControlCenter owns model settings and spend; SecurityCenter owns dated evidence and findings; Credentials owns encrypted handoffs. BonusAvailability exposes accounting, historical edits, and Excel exports in every environment. Time/work, activity audit, employee access and Docs retain their own reads/actions. `src/App.js` owns route admission; backend endpoints independently enforce administrator permission. Changing a tab executes no management action.
 */

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AppTab, AppTabView } from '../shared/AppTabView';
import AiControlCenter from './AiControlCenter';
import People from './People';
import EmployeeAccess from './EmployeeAccess';
import PhoneAdmin from './PhoneAdmin';
import PhoneDashboard from './PhoneDashboard';
import Docs from './Docs';
import Credentials from './Credentials';
import SecurityCenter from './SecurityCenter';
import UsageAuditDashboard from './UsageAuditDashboard';
import WorkComparison from './WorkComparison';
import Bonuses from './Bonuses/BonusAvailability';
import useUrlTabState from '../shared/useUrlTabState';
import './Admin.css';

const ADMIN_TAB_IDS = [
    'people',
    'work-comparison',
    'bonuses',
    'phone',
    'employee-access',
    'phone-dashboard',
    'ai',
    'usage-audit',
    'security',
    'credentials',
    'docs',
];

const SETTINGS_TABS = new Set(['intake', 'notification-rules', 'case-attribute-types']);
const LEGACY_AI_TABS = {
    'openai-spend': { view: 'spend', provider: 'openai' },
    'anthropic-spend': { view: 'spend', provider: 'anthropic' },
    'document-ai': { view: 'features', feature: 'document-ai-edits', panel: 'access' },
};

const LEGACY_TAB_ALIASES = {
    'employee-lifecycle': 'people',
    'employees': 'people',
    'attorneys': 'people',
    'attorney-rates': 'people',
}



function Admin() {
    const location = useLocation();
    const { activeIndex: activeTab, onTabChange } = useUrlTabState(ADMIN_TAB_IDS, {
        aliases: LEGACY_TAB_ALIASES,
    });

    useEffect(() => {
        document.title = "Admin"
    }, [])

    const isBonuses = ADMIN_TAB_IDS[activeTab] === 'bonuses';
    const isPeople = ADMIN_TAB_IDS[activeTab] === 'people';
    const isWork = ADMIN_TAB_IDS[activeTab] === 'work-comparison';
    const isAudit = ADMIN_TAB_IDS[activeTab] === 'usage-audit';
    const isPhone = ADMIN_TAB_IDS[activeTab] === 'phone';
    const isAi = ADMIN_TAB_IDS[activeTab] === 'ai';
    const isCredentials = ADMIN_TAB_IDS[activeTab] === 'credentials';
    const isSecurity = ADMIN_TAB_IDS[activeTab] === 'security';
    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (SETTINGS_TABS.has(requestedTab)) {
        return <Navigate to={`/settings?tab=${requestedTab}`} replace />;
    }
    if (LEGACY_AI_TABS[requestedTab]) {
        const next = new URLSearchParams(location.search);
        next.set('tab', 'ai');
        next.delete('feature');
        next.delete('panel');
        Object.entries(LEGACY_AI_TABS[requestedTab]).forEach(([key, value]) => next.set(key, value));
        return <Navigate to={{ pathname: location.pathname, search: `?${next}`, hash: location.hash }} replace />;
    }

    return (
        <div className={`admin-route${isBonuses ? ' admin-route--bonuses' : isPeople ? ' admin-route--people' : isWork ? ' admin-route--work' : isAudit ? ' admin-route--audit' : isPhone ? ' admin-route--phone' : isAi ? ' admin-route--ai' : isCredentials ? ' admin-route--credentials' : isSecurity ? ' admin-route--security' : ''}`}>
            <AppTabView
                activeIndex={activeTab}
                onTabChange={onTabChange}
                variant="route"
            >
                <AppTab header="People">
                    <People embedded />
                </AppTab>
                <AppTab header="Time & work">
                    <WorkComparison />
                </AppTab>
                <AppTab header="Bonuses">
                    <Bonuses />
                </AppTab>
                <AppTab header="Phone">
                    <PhoneAdmin />
                </AppTab>
                <AppTab header="Employee access">
                    <EmployeeAccess />
                </AppTab>
                <AppTab header="Phone Dashboard">
                    <PhoneDashboard />
                </AppTab>
                <AppTab header="AI">
                    <AiControlCenter />
                </AppTab>
                <AppTab header="Activity audit">
                    <UsageAuditDashboard embedded />
                </AppTab>
                <AppTab header="Security">
                    <SecurityCenter />
                </AppTab>
                <AppTab header="Credentials">
                    <Credentials />
                </AppTab>
                <AppTab header="Docs">
                    <Docs embedded />
                </AppTab>
            </AppTabView>
        </div>
    )
}

export default Admin
